import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { formatDateDb } from "../utils/tools";
import { customDateFormatType } from "../utils/utils";

const NUMBER_NFT_PER_PAGE = 25;
const queryKey = "employeeStopWorking";

async function fetchEmployeeStopWorking(
  currentPage,
  search,
  employeeId,
  startTime,
  endTime,
  saleBlock,
  saleArea,
  department,
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (employeeId) options.employeeId = employeeId;
    if (startTime) options.fromEndDate = customDateFormatType(startTime);
    if (endTime) options.toEndDate = customDateFormatType(endTime);
    if (saleBlock) options.saleBlockId = saleBlock;
    if (saleArea) options.saleAreaId = saleArea;
    if (department) options.department = department;

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI(
      "get",
      `/employee/stopWorking/list?${queryParams}`,
    );
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries,
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        code: item?.employeeObject?.code || item?.employee?.code || "-",
        name: item?.employeeObject?.fullName || item?.employee?.fullName || "-",
        branch: item?.employee?.branch?.name || "-",
        saleBlock: item?.employee?.saleBlock?.name || "-",
        saleArea: item?.employee?.saleArea?.name || "-",
        supportDepartment: item?.employee?.supportDepartment?.name || "-",
        jobPosition: item?.employee?.jobPosition?.name || "-",
        endDate: item?.endDate ? formatDateDb(item?.endDate) : "-",
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching checkIn data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useEmployeeStopWorking(
  search,
  employeeId,
  startTime,
  endTime,
  saleBlockId,
  saleAreaId,
  department,
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [
      queryKey,
      currentPage,
      search,
      employeeId,
      startTime,
      endTime,
      saleBlockId,
      saleAreaId,
      department,
    ],
    queryFn: () =>
      fetchEmployeeStopWorking(
        currentPage,
        search,
        employeeId,
        startTime,
        endTime,
        saleBlockId,
        saleAreaId,
        department,
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    employeeStopWorking: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
